/**
 * api.ts
 * ---------------------------------
 * This file contains frontend API hooks for the "tags" module. It leverages
 * React Query for asynchronous data handling and caching.
 *
 * Available hooks:
 *   1) useTags(productId) - (Already implemented) Retrieves all tags for a product.
 *   2) useImportTags() - Imports tags from a CSV file. Developer-only endpoint.
 *   3) useAssociateTag() - Associates or dissociates a tag with an item (document or file).
 */

import { ApiContext } from '@shared/contexts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { TAssociatedItem, TAssociateParams, TTag } from './types';

enum EOperation {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  UPDATE = 'UPDATE',
}

/**
 * Helper: updateTagQueryData
 * ---------------------------------
 * Updates the tags query data based on the operation (add/remove).
 *
 * @param queryClient - The React Query client.
 * @param operation - The operation type (add/remove).
 * @param productId - The product ID.
 * @param tagId - The tag ID.
 * @param itemId - The item ID.
 * @param associatedItemResponseData - The associated item response data.
 * @returns The updated tags query data.
 */
const updateTagQueryData = (
  queryClient: any,
  operation: EOperation,
  productId: string,
  tagId: string,
  itemId: string,
  associatedItemResponseData: TAssociatedItem
) => {
  const updateAssociatedItems = (associatedItems: TAssociatedItem[]) => {
    if (operation === EOperation.REMOVE)
      return associatedItems.filter((associatedItem) => associatedItem.item._id !== itemId);

    if (operation === EOperation.UPDATE) {
      return associatedItems.map((associatedItem) => {
        if (associatedItem.item._id === itemId) return associatedItemResponseData;
        return associatedItem;
      });
    }

    if (operation === EOperation.ADD) return [associatedItemResponseData, ...associatedItems];
    return associatedItems;
  };

  const updateTags = (tags: TTag[]) => {
    return tags.map((tag) => {
      if (tag._id === tagId) {
        return {
          ...tag,
          associatedItems: updateAssociatedItems(tag.associatedItems),
        };
      }
      return tag;
    });
  };

  return queryClient.setQueryData([`tags-${productId}`], (oldData: TTag[] | undefined): TTag[] | undefined => {
    return oldData ? updateTags(oldData) : oldData;
  });
};

/**
 * Hook: useTags
 * ---------------------------------
 * Retrieves all tags for a product.
 *
 * Usage:
 *  const { data, isLoading, error } = useTags(productId);
 *
 * Endpoint:
 *  GET /tags?product=:productId
 *
 * @param productId - The product ID to fetch tags for.
 * @returns An object with the tags data, loading state, and error.
 */

export const useTags = (productId: string) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const getTags = async (): Promise<TTag[]> => apiClient.get(`/tags?product=${productId}`).then((res) => res.data);

  const { data, isError, error, isLoading } = useQuery({
    retry,
    queryKey: [`tags-${productId}`],
    queryFn: getTags,
  });

  useHandleQueryError(isError, error, `Failed to load tags for ${productId}!`, `Loading tags failed!`);

  return { error, data, isLoading };
};

/**
 * Hook: useImportTags
 * ---------------------------------
 * Developer-only endpoint to import tags from a CSV file.
 *
 * Usage:
 *   const importTagsMutation = useImportTags(productId);
 *   const handleImport = (file: File) => {
 *     const formData = new FormData();
 *     formData.append('file', file);
 *     importTagsMutation.mutate(formData);
 *   };
 *
 * Endpoint:
 *   POST /tags
 *
 * @param productId - The product ID to import tags for.
 * @returns A mutation object with success/error callbacks.
 */
export const useImportTags = (productId: string) => {
  const { apiClient, show, queryClient, handleApiError } = useContext(ApiContext);

  // The mutation function that calls our backend
  const mutationFn = async (csvFormData: FormData) => {
    // Expecting the CSV file to be appended as "file"
    await apiClient.post('/tags', csvFormData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  // Use the mutation with success/error callbacks
  return useMutation({
    mutationFn,
    onSuccess: () => {
      show('success', 'Tags imported successfully.');
      // Invalidate tags queries for an up-to-date list
      queryClient.invalidateQueries({ queryKey: [`tags-${productId}`] });
    },
    onError: handleApiError('Importing tags failed!'),
  });
};

/**
 * Hook: useAssociateTag
 * ---------------------------------
 * Associates or dissociates a tag with an item (document or file).
 * The same endpoint handles both full-item association, specific headings,
 * or complete removal (dissociation).
 *
 * Usage:
 *   const associateTagMutation = useAssociateTag(productId);
 *   associateTagMutation.mutate({
 *     tagId: '...',
 *     itemId: '...',
 *     itemtype: 'DOCUMENT',  // or 'FILE'
 *     headingIds: [...],     // optional array of heading IDs
 *     dissociateItem: false          // or true for dissociation
 *   });
 *
 * Endpoint:
 *   PUT /tags/:id/associate
 *
 * @param productId - The product ID to associate the tag with.
 * @returns A mutation object with success/error callbacks.
 */

export const useAssociateTag = (productId: string, tagsData?: TTag[]) => {
  const { apiClient, queryClient, show, handleApiError } = useContext(ApiContext);

  const mutationFn = async ({
    tagId,
    itemId,
    itemtype,
    headingIds,
    dissociateItem,
  }: TAssociateParams): Promise<TAssociatedItem> => {
    /**
     * The backend’s “associate” endpoint can either associate or dissociate
     * depending on the request body. For instance, `dissociateItem: true` will
     * dissociate the item from the tag.
     *
     * The endpoint also supports updating the associated item with new headings.
     * If `headingIds` are provided, the backend will update the item’s associated
     * headings with the new list. If `headingIds` is undefined, the backend will
     * associate the whole item.
     */
    const url = `/tags/${tagId}/associate`;
    return apiClient
      .put(url, {
        itemId,
        itemtype,
        headingIds,
        dissociateItem,
      })
      .then((res) => res.data);
  };

  return useMutation({
    mutationFn,
    onSuccess: async (associatedItemResponseData, variables) => {
      const isUpdate = Boolean(
        tagsData
          ?.find((tag) => tag._id === variables.tagId)
          ?.associatedItems.find((associatedItem) => associatedItem.item._id === variables.itemId)
      );

      let operation;

      /**
       * Show a success message based on the operation type.
       * Update the tags query data based on the operation.
       *
       * Important: Check the `dissociateItem` first to avoid false positives for updates.
       */
      if (variables.dissociateItem) {
        show('success', 'Item dissociated.');
        operation = EOperation.REMOVE;
      } else if (isUpdate) {
        show('success', 'Association updated.');
        operation = EOperation.UPDATE;
      } else {
        show('success', 'Item associated with the tag.');
        operation = EOperation.ADD;
      }

      // Update the tags query data
      updateTagQueryData(
        queryClient,
        operation,
        productId,
        variables.tagId,
        variables.itemId,
        associatedItemResponseData
      );
    },
    onError: handleApiError('Updating tag association failed!'),
  });
};
